import {isFeatureEnabled} from 'feature-flags';
import {Menu} from 'platform/components';

import {useLocation} from 'react-router-dom';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {usePermissions} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import {useCodeLists} from '../../../hooks/useCodeLists';
import {NavigationItem} from '../../../types/NavigationItem';

export function SettingsNavigation() {
  const navigate = useNavigate();
  const {pathname} = useLocation();

  const {data: codelists} = useCodeLists();

  const [
    canReadVehicleWarehouse,
    canReadEmployee,
    canReadPneuhotel,
    canReadServiceCase,
    canReadCustomFieldsDefinitions,
    canReadArticle,
    canReadBusinessCase,
    canReadVehicle,
    canUpdateRole,
    canUpdateUser,
    warehouseDictionaryRead,
    canReadMandatoryFields,
    canReadTenantMultiFactorAuthenticationSettings,
    canReadRentalVehicle,
    canReadTeam,
    canReadFacebookLeads,
    canReadReporting,
    canReadAiLeads,
  ] = usePermissions({
    permissionKeys: [
      'vehicleWarehouseRead',
      'employeeRead',
      'pneuhotelRead',
      'serviceCaseRead',
      'readCustomFieldsDefinitions',
      'articleRead',
      'businessCaseRead',
      'vehicleRead',
      'updateRole',
      'updateUser',
      'warehouseDictionaryRead',
      'readMandatoryFields',
      'canReadTenantMultiFactorAuthenticationSettings',
      'readRentalVehicle',
      'teamRead',
      'readFacebookLeads',
      'reportingRead',
      'aiLeadSettingsRead',
    ],
  });

  const isWorkShopEnabled = canReadServiceCase;
  const isSalesModuleEnabled = canReadBusinessCase;
  const isMfaEnabled =
    isFeatureEnabled(featureFlags.SETTINGS_TWO_FACTOR_AUTH) &&
    canReadTenantMultiFactorAuthenticationSettings;

  const menuItems = buildArray<NavigationItem>([])
    .when(isSalesModuleEnabled, {
      id: 'sales',
      label: i18n.t('page.salesSettings.title'),
      leftIcon: 'action/assessment',
      items: buildArray<NavigationItem>([])
        .add({
          id: 'advertising',
          label: i18n.t('page.salesSettings.labels.advertising'),
          href: settingsRoutes.advertising,
        })
        .add({
          id: 'businessCases',
          label: i18n.t('page.businessCases.title'),
          href: settingsRoutes.businessCase,
        })
        .add({
          id: 'interest',
          label: i18n.t('entity.interest.labels.interest'),
          href: settingsRoutes.interest,
        })
        .add({
          id: 'productCatalogue',
          label: i18n.t('entity.productCatalog.labels.productCatalog'),
          href: settingsRoutes.productCatalog,
        })
        .add({
          id: 'codelists',
          label: i18n.t('page.codelists.pageTitle'),
          href: settingsRoutes.codelists,
        })
        .add({
          id: 'salesServiceOrderVariants',
          label: i18n.t('entity.order.labels.serviceOrderVariants'),
          href: settingsRoutes.salesServiceOrderVariants,
        })
        .when(
          canReadVehicleWarehouse &&
            isFeatureEnabled(featureFlags.SALES_VEHICLE_WAREHOUSE_MANAGEMENT),
          {
            id: 'vehicleWarehouseManagement',
            label: i18n.t('page.salesSettings.labels.vehicleWarehouseManagement'),
            href: settingsRoutes.vehicleWarehouseManagement,
          }
        )
        .when(isFeatureEnabled(featureFlags.SALES_AUTOCANCEL_RESERVATION), {
          id: 'reservation',
          label: i18n.t('page.reservationSettings.labels.pageTitle'),
          href: settingsRoutes.reservation,
        })
        .when(isFeatureEnabled(featureFlags.SALES_COSTS_WITH_VAT), {
          id: 'financing',
          label: i18n.t('entity.financing.labels.pageTitle'),
          href: settingsRoutes.financing,
        })
        .when(isFeatureEnabled(featureFlags.CHAT_FEATURE), {
          id: 'chatSettings',
          label: i18n.t('entity.chatSettings.labels.pageTitle'),
          href: settingsRoutes.chatSettings,
        })
        .when(isFeatureEnabled(featureFlags.AI_LEAD_MANAGEMENT) && canReadAiLeads, {
          id: 'aiLeadManagement',
          label: i18n.t('page.settings.labels.aiLeadManagement'),
          href: settingsRoutes.aiLeadManagement,
        })
        .when(
          canReadAiLeads && isSalesModuleEnabled && isFeatureEnabled(featureFlags.AI_LEAD_LOG),
          {
            id: 'aiLeadManagementHistory',
            label: i18n.t('page.settings.labels.aiLeadEmailHistory'),
            href: settingsRoutes.aiLeadManagementHistory,
          }
        )
        .when(isFeatureEnabled(featureFlags.ONLINE_OFFER_SETTINGS), {
          id: 'digitalDeal',
          label: i18n.t('page.settings.labels.digitalDeal'),
          href: settingsRoutes.digitalDeal,
        }),
    })
    .when(canReadVehicle, {
      id: 'vehicle',
      label: i18n.t('entity.vehicle.labels.vehicle'),
      leftIcon: 'maps/directions_car',
      items: [
        {
          id: 'topMakes',
          label: i18n.t('general.labels.topMakes'),
          href: settingsRoutes.vehicleTopMakes,
        },
        {
          id: 'vehicleSeriesDefinition',
          label: i18n.t('page.salesSettings.labels.vehicleSeriesDefinitionTitle'),
          href: settingsRoutes.vehicleSeriesDefinition,
        },
        {
          id: 'inspections',
          label: i18n.t('entity.vehicle.labels.inspections'),
          href: settingsRoutes.vehicleInspections,
        },
        {
          id: 'condition',
          label: i18n.t('page.vehicleSettings.labels.condition'),
          href: settingsRoutes.vehicleCondition,
        },
        {
          id: 'parking',
          label: i18n.t('page.vehicleSettings.labels.parking'),
          href: settingsRoutes.vehicleParking,
        },
      ],
    })
    .when(canReadRentalVehicle, {
      id: 'rentalVehicle',
      label: i18n.t('entity.rental.labels.rental'),
      leftIcon: 'automotive/rental',
      items: [
        {
          id: 'priceGroupList',
          label: i18n.t('entity.rental.labels.priceGroups'),
          href: settingsRoutes.priceGroupList,
        },
        {
          id: 'carRentalSeries',
          label: i18n.t('entity.rental.labels.carRentalSeries'),
          href: settingsRoutes.carRentalSeries,
        },
        ...(codelists?.rental?.map((codeList) => ({
          id: codeList.codelistId,
          label: codeList.name,
          href: composePath(settingsRoutes.editRentalCodeList, {params: {id: codeList.codelistId}}),
        })) ?? []),
      ],
    })
    .when(canReadVehicle, {
      id: 'customer',
      label: i18n.t('entity.customer.labels.customer'),
      leftIcon: 'social/person',
      items: buildArray<NavigationItem>([])
        .add({
          id: 'customerCodeDefinition',
          label: i18n.t('entity.seriesDefinition.labels.customerCodeDefinition'),
          href: settingsRoutes.customerCodeDefinition,
        })
        .when(isWorkShopEnabled, {
          id: 'bankTransferSettings',
          label: i18n.t('page.settings.bankTransfer.navigation'),
          href: settingsRoutes.bankTransferSettings,
        }),
    })
    .whenFeatureEnabled(featureFlags.SETTINGS_MANAGEMENT_SPLIT, {
      id: 'userManagement',
      label: i18n.t('page.settings.labels.userManagement'),
      leftIcon: 'social/people',
      items: buildArray<NavigationItem>([])
        .add({
          id: 'users',
          label: i18n.t('entity.user.labels.users'),
          href: settingsRoutes.userManagement,
          isDisabled: !canUpdateUser,
          leftIcon: canUpdateUser ? undefined : 'action/lock',
        })
        .add({
          id: 'roles',
          label: i18n.t('entity.person.labels.roles'),
          href: settingsRoutes.roleManagement,
          isDisabled: !canUpdateRole,
          leftIcon: canUpdateRole ? undefined : 'action/lock',
        })
        .when(isFeatureEnabled(featureFlags.CORE_TEAMS) && canReadTeam, {
          id: 'teamManagement',
          label: i18n.t('page.settings.labels.teamManagement'),
          href: settingsRoutes.teamManagement,
        }),
    })
    .whenFeatureNotEnabled(featureFlags.SETTINGS_MANAGEMENT_SPLIT, {
      id: 'userManagement',
      label: i18n.t('page.settings.labels.userManagement'),
      leftIcon: 'social/people',
      isDisabled: !canUpdateRole && !canUpdateUser,
      href: settingsRoutes.userManagement,
    })
    .whenFeatureNotEnabled(featureFlags.AUTH_PROFILES_TEMPLATES, {
      id: 'documentTemplates',
      label: i18n.t('entity.document.labels.documentTemplates'),
      leftIcon: 'action/description',
      href: settingsRoutes.documentTemplates,
    })
    .whenFeatureEnabled(featureFlags.AUTH_PROFILES_TEMPLATES, {
      id: 'templateManagement',
      label: i18n.t('page.settings.labels.templateManagement'),
      leftIcon: 'action/description',
      href: settingsRoutes.templateManagement,
    })
    .add({
      id: 'gdrpConsentTypes',
      label: i18n.t('entity.gdpr.labels.gdprConsentTypes'),
      leftIcon: 'content/ballot',
      href: settingsRoutes.gdprConsentTypes,
      hasSeparator: !isWorkShopEnabled,
    })
    .when(isWorkShopEnabled, {
      id: 'workshop',
      label: i18n.t('module.workshop.title'),
      leftIcon: 'action/build',
      items: buildArray<NavigationItem>([])
        .add({
          id: 'dictionaries',
          label: i18n.t('page.settings.labels.dictionaries'),
          items: [
            {
              id: 'workType',
              label: i18n.t('entity.order.labels.workType'),
              href: settingsRoutes.workType,
            },
            {
              id: 'vehicleGroup',
              label: i18n.t('entity.workshop.labels.vehicleGroups'),
              href: settingsRoutes.vehicleGroups,
            },
            {
              id: 'workshopUnits',
              label: i18n.t('entity.workshop.labels.units'),
              href: settingsRoutes.workshopUnits,
            },
            {
              id: 'workshopVehicleClasses',
              label: i18n.t('entity.workshop.labels.vehicleClasses'),
              href: settingsRoutes.workshopVehicleClasses,
            },
            {
              id: 'workshopInsuranceTypes',
              label: i18n.t('entity.workshop.labels.insuranceTypes'),
              href: settingsRoutes.workshopInsuranceTypes,
            },
          ],
        })
        .add({
          id: 'labourRates',
          label: i18n.t('entity.workshop.labels.labourRates'),
          href: settingsRoutes.labourRates,
        })
        .add({
          id: 'labourCatalogues',
          label: i18n.t('entity.workshop.labels.labourCatalogues'),
          href: settingsRoutes.labourCatalogues,
        })
        .whenFeatureEnabled(featureFlags.WORKSHOP_INTEREST, {
          id: 'workshopInterest',
          label: i18n.t('entity.interest.labels.interest'),
          href: settingsRoutes.workshopInterest,
        })
        .add({
          id: 'serviceCaseType',
          label: i18n.t('entity.workshopPresetsCategory.labels.serviceCaseType'),
          href: settingsRoutes.serviceCaseType,
        })
        .add({
          id: 'workshopServiceOrderVariants',
          label: i18n.t('entity.order.labels.serviceOrderVariants'),
          href: settingsRoutes.serviceOrderVariants,
        })
        .add({
          id: 'servicePackages',
          label: i18n.t('entity.workshopServicePackages.labels.title'),
          href: settingsRoutes.workshopServicePackages,
        })
        .add({
          id: 'checkinInspection',
          label: i18n.t('entity.inspection.labels.checkinInspection'),
          href: settingsRoutes.checkinInspection,
        })
        .add({
          id: 'handoverInspection',
          label: i18n.t('entity.inspection.labels.handoverInspection'),
          href: settingsRoutes.handoverInspection,
        })
        .add({
          id: 'workshopCustomerGroups',
          label: i18n.t('entity.rabatDiscounts.labels.rabatDiscounts'),
          href: settingsRoutes.workshopCustomerGroups,
        })
        .add({
          id: 'workshopSmallUnitMaterial',
          label: i18n.t('entity.workshopMinorUnitMaterial.labels.minorUnitMaterial'),
          href: settingsRoutes.workshopSmallUnitMaterial,
        })
        .add({
          id: 'customerContracts',
          label: i18n.t('entity.customerContract.labels.customerContracts'),
          href: settingsRoutes.customerContracts,
        })
        .add({
          id: 'workshopMeasurementUnits',
          label: i18n.t('page.settings.labels.measurementUnits'),
          href: settingsRoutes.workshopMeasurementUnits,
        })
        .add({
          id: 'workshopServiceOrderGroups',
          label: i18n.t('page.settings.labels.serviceOrderGroups'),
          href: settingsRoutes.workshopServiceOrderGroups,
        })
        .add({
          id: 'workshopSelectableMaterials',
          label: i18n.t('page.settings.labels.selectableMaterials'),
          href: settingsRoutes.workshopSelectableMaterials,
        }),
    })
    .when(canReadArticle && warehouseDictionaryRead, {
      id: 'warehouseManagement',
      label: i18n.t('page.warehouseSettings.labels.warehouseSettings'),
      leftIcon: 'custom/warehouse',
      items: buildArray<NavigationItem>([])
        .when(warehouseDictionaryRead, {
          id: 'dictionaries',
          label: i18n.t('page.settings.labels.dictionaries'),
          items: buildArray<NavigationItem>([
            {
              id: 'suppliers',
              label: i18n.t('entity.warehouse.labels.suppliers'),
              href: settingsRoutes.suppliers,
            },
            {
              id: 'manufacturers',
              label: i18n.t('entity.warehouse.labels.manufacturers'),
              href: settingsRoutes.warehousesManufacturers,
            },
            {
              id: 'measurementUnits',
              label: i18n.t('entity.warehouse.labels.measurementUnits'),
              href: settingsRoutes.warehousesMeasurementUnits,
            },
            {
              id: 'dispensingUnitsPresets',
              label: i18n.t('entity.warehouse.labels.dispensingUnitsPresets'),
              href: settingsRoutes.warehousesDispensingUnitsPresets,
            },
            {
              id: 'marketingCode',
              label: i18n.t('entity.warehouse.labels.marketingCode'),
              href: settingsRoutes.warehousesMarketingCode,
            },
            {
              id: 'warehouses/article-category',
              label: i18n.t('entity.warehouse.labels.category'),
              href: settingsRoutes.warehousesArticleCategory,
            },
            {
              id: 'warehouses/supplier-discount-groups',
              label: i18n.t('entity.warehouse.labels.supplierDiscounts'),
              href: settingsRoutes.warehousesSupplierDiscountGroups,
            },
          ]),
        })
        .when(warehouseDictionaryRead, {
          id: 'warehouses',
          label: i18n.t('page.warehouses.labels.title'),
          href: settingsRoutes.warehousesList,
        })
        .when(warehouseDictionaryRead, {
          id: 'warehouses/supplierOrderVariants',
          label: i18n.t('page.warehouses.labels.supplierOrderVariants'),
          href: settingsRoutes.warehousesSupplierOrderVariants,
        })
        .when(warehouseDictionaryRead, {
          id: 'warehouses/direct-sale-variants',
          label: i18n.t('entity.warehouse.labels.directSaleVariants'),
          href: settingsRoutes.warehousesDirectSaleVariants,
        })
        .when(warehouseDictionaryRead, {
          id: 'warehouses/transfer-order-variants',
          label: i18n.t('entity.warehouse.labels.transferOrderVariants'),
          href: settingsRoutes.warehousesTransferOrderVariants,
        })
        .when(warehouseDictionaryRead, {
          id: 'warehouses/spaces',
          label: i18n.t('entity.warehouse.labels.spaces'),
          href: settingsRoutes.warehousesSpaces,
        }),
    })
    .when(canReadEmployee, {
      id: 'employee',
      label: i18n.t('module.employee.title'),
      leftIcon: 'custom/employee',
      hasSeparator: canReadPneuhotel,
      items: [
        {
          id: 'professions',
          label: i18n.t('entity.employeeProfessions.labels.title'),
          href: settingsRoutes.employeeProfessions,
        },
        {
          id: 'competences',
          label: i18n.t('entity.employeeCompetences.labels.title'),
          href: settingsRoutes.employeeCompetences,
        },
        {
          id: 'costCenters',
          label: i18n.t('entity.employeeCostCenters.labels.title'),
          href: settingsRoutes.employeeCostCenters,
        },
        {
          id: 'workTeams',
          label: i18n.t('entity.employeeWorkTeams.labels.title'),
          href: settingsRoutes.employeeWorkTeams,
        },
      ],
    })
    .when(canReadPneuhotel, {
      id: 'tiresInventory',
      label: i18n.t('module.tiresInventory.title'),
      leftIcon: 'modules/pneu_hotel',
      items: [
        {
          id: 'dictionaries',
          label: i18n.t('page.settings.labels.dictionaries'),
          items: [
            {
              id: 'tireCategory',
              label: i18n.t('entity.tiresInventory.labels.tireCategory'),
              href: settingsRoutes.tireCategory,
            },
            {
              id: 'tiresInventory-manufacturers',
              label: i18n.t('entity.tiresInventory.labels.tireManufacturers'),
              href: settingsRoutes.tireManufacturers,
            },
            {
              id: 'tireWidth',
              label: i18n.t('entity.tiresInventory.labels.tireWidth'),
              href: settingsRoutes.tireWidth,
            },
            {
              id: 'tireAspectRatio',
              label: i18n.t('entity.tiresInventory.labels.tireAspectRatio'),
              href: settingsRoutes.tireAspectRatio,
            },
            {
              id: 'tireRimSize',
              label: i18n.t('entity.tiresInventory.labels.tireRimSize'),
              href: settingsRoutes.tireRimSize,
            },
            {
              id: 'tireLoadIndex',
              label: i18n.t('entity.tiresInventory.labels.tireLoadIndex'),
              href: settingsRoutes.tireLoadIndex,
            },
            {
              id: 'tireSpeedRatio',
              label: i18n.t('entity.tiresInventory.labels.tireSpeedRatio'),
              href: settingsRoutes.tireSpeedRatio,
            },
            {
              id: 'tiresInventory-locations',
              label: i18n.t('entity.tiresInventory.labels.tireLocations'),
              href: settingsRoutes.tireLocations,
            },
            {
              id: 'tireCondition',
              label: i18n.t('entity.tiresInventory.labels.tireCondition'),
              href: settingsRoutes.tireCondition,
            },
          ],
        },
        {
          id: 'tiresInventory-general',
          label: i18n.t('general.labels.general'),
          href: settingsRoutes.tiresInventory,
        },
        {
          id: 'tireWarehouses',
          label: i18n.t('entity.tireWarehouses.labels.title'),
          href: settingsRoutes.tireWarehouses,
        },
        {
          id: 'tireTransit',
          label: i18n.t('entity.tireTransit.labels.title'),
          href: settingsRoutes.tireTransit,
        },
      ],
    })
    .when(canReadReporting, {
      id: 'reporting',
      label: i18n.t('page.settings.labels.reporting'),
      leftIcon: 'sidebar/analytics',
      hasSeparator: !isMfaEnabled,
      items: [
        {
          id: 'mobisIris',
          label: i18n.t('page.settings.labels.mobisIris'),
          items: [
            {
              id: 'partDepartment',
              label: i18n.t('entity.partDepartment.title'),
              href: settingsRoutes.partDepartment,
            },
          ],
        },
      ],
    })
    .when(isMfaEnabled, {
      id: 'twoFactorSettings',
      label: i18n.t('page.settings.labels.security'),
      hasSeparator: true,
      leftIcon: 'hardware/security',
      href: settingsRoutes.twoFactorSettings,
    })
    .whenFeatureNotEnabled(featureFlags.TENANT_V2, {
      id: 'tenantProfile',
      label: i18n.t('page.settings.labels.tenantProfile'),
      leftIcon: 'places/business_center',
      href: settingsRoutes.tenant,
    })
    .whenFeatureNotEnabled(featureFlags.TENANT_V2, {
      id: 'billingInformation',
      label: i18n.t('entity.checkout.labels.billingInformation'),
      leftIcon: 'action/credit_card',
      href: settingsRoutes.billingInformation,
    })
    .whenFeatureEnabled(featureFlags.TENANT_V2, {
      id: 'tenantManagement',
      label: i18n.t('entity.checkout.labels.tenantManagement'),
      leftIcon: 'places/business_center',
      items: buildArray<NavigationItem>()
        .add({
          id: 'tenantProfile',
          label: i18n.t('page.settings.labels.tenantProfile'),
          leftIcon: 'places/business_center',
          href: settingsRoutes.tenant,
        })
        .add({
          id: 'billingInformation',
          label: i18n.t('entity.checkout.labels.billingInformation'),
          leftIcon: 'action/credit_card',
          href: settingsRoutes.billingInformation,
        })
        .add({
          id: 'tenantLogos',
          label: i18n.t('page.settings.labels.tenantLogs'),
          leftIcon: 'image/photo',
          href: settingsRoutes.tenantLogos,
        }),
    })
    .add({
      id: 'general',
      label: i18n.t('page.generalSettings.labels.title'),
      leftIcon: 'action/dns',
      items: buildArray<NavigationItem>()
        .add({
          id: 'seriesDefinition',
          label: i18n.t('page.generalSettings.labels.seriesDefinitionLink'),
          href: settingsRoutes.seriesDefinition,
        })
        .whenFeatureEnabled(featureFlags.DIGITAL_SIGNATURE, {
          id: 'digital_signature',
          label: i18n.t('entity.settings.labels.signatures'),
          href: settingsRoutes.signatures,
        })
        .whenFeatureEnabled(featureFlags.MESSAGE_TEMPLATES, {
          id: 'messageTemplates',
          label: i18n.t('page.messageTemplates.labels.list'),
          href: settingsRoutes.messageTemplates,
        })
        .whenFeatureEnabled(featureFlags.SCHEDULED_MESSAGES, {
          id: 'scheduledMessages',
          label: i18n.t('page.settings.labels.scheduledMessages'),
          href: settingsRoutes.scheduledMessages,
        })
        .when(
          isFeatureEnabled(featureFlags.VEHICLE_CUSTOM_FIELDS) && canReadCustomFieldsDefinitions,
          {
            id: 'customFields',
            label: i18n.t('page.generalSettings.labels.customFields'),
            href: settingsRoutes.customFields,
          }
        )
        .whenFeatureEnabled(featureFlags.NOTIFICATION_SETTINGS, {
          id: 'notifications',
          label: i18n.t('page.settings.notifications.title'),
          href: settingsRoutes.notificationsOverview,
        })
        .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
          id: 'generalChecklist',
          label: i18n.t('page.settings.generalChecklist.title'),
          href: settingsRoutes.generalChecklist,
        })
        .when(
          canReadMandatoryFields && isFeatureEnabled(featureFlags.CONFIGURABLE_MANDATORY_FIELDS),
          {
            id: 'mandatoryFields',
            label: i18n.t('entity.mandatoryFields.title'),
            href: settingsRoutes.mandatoryFields,
          }
        ),
    })
    .add({
      id: 'branches',
      label: i18n.t('entity.branch.labels.branches'),
      leftIcon: 'social/domain',
      href: settingsRoutes.branches,
    })
    .add({
      id: 'accountingDocuments',
      label: i18n.t('page.accounting.labels.accounting'),
      leftIcon: 'editor/insert_drive_file',
      hasSeparator: true,
      items: buildArray<NavigationItem>([
        {
          id: 'cashRegisters',
          label: i18n.t('page.accounting.labels.cashRegisters'),
          href: settingsRoutes.cashRegisters,
        },
        {
          id: 'cashOperationsLimits',
          label: i18n.t('page.accounting.labels.cashRegisterLimitsTitle'),
          href: settingsRoutes.cashLimits,
        },
        {
          id: 'invoiceDefinition',
          label: i18n.t('page.accounting.labels.invoiceDefinition'),
          href: settingsRoutes.invoiceDefinition,
        },
        {
          id: 'documentRoundingDefinitions',
          label: i18n.t('page.accounting.labels.documentRoundingDefinitions'),
          href: settingsRoutes.documentRoundingDefinitions,
        },
      ])
        .whenFeatureEnabled(featureFlags.ACCOUNTING_CANCEL_V2, {
          id: 'invoiceCanceling',
          label: i18n.t('page.accounting.labels.invoiceCanceling'),
          href: settingsRoutes.invoiceCanceling,
        })
        .whenFeatureEnabled(featureFlags.ACCOUNTING_MARGIN_INVOICES, {
          id: 'invoiceMargin',
          label: i18n.t('page.accountingSettings.labels.invoiceMargin'),
          href: settingsRoutes.invoiceMarginOverview,
        })
        .add({
          id: 'proformaGeneration',
          label: i18n.t('page.accountingSettings.labels.proformaGeneration'),
          href: settingsRoutes.proformaGeneration,
        })
        .add({
          id: 'proformaCreation',
          label: i18n.t('page.accountingSettings.labels.proformaCreation'),
          href: settingsRoutes.ProformaCreationOverview,
        })
        .add({
          id: 'correctiveTaxDocuments',
          label: i18n.t('page.accountingSettings.labels.correctiveTaxDocumentsSettings'),
          href: settingsRoutes.correctiveTaxDocuments,
        })
        .add({
          id: 'vatTotalAndCalculation',
          label: i18n.t('page.accountingSettings.labels.vatTotalAndCalculation'),
          href: settingsRoutes.vatTotalAndCalculationOverview,
        })
        .add({
          id: 'taxDocumentForPayment',
          label: i18n.t('entity.accounting.labels.taxDocumentForPayment'),
          items: [
            {
              id: 'taxDocumentForPayment-issuedType',
              label: i18n.t('page.accountingSettings.labels.issuedType'),
              href: settingsRoutes.taxDocumentIssuedType,
            },
            {
              id: 'taxDocumentForPayment-importType',
              label: i18n.t('page.accountingSettings.labels.importType'),
              href: settingsRoutes.taxDocumentImportType,
            },
          ],
        })
        .add({
          id: 'qrCodeSettings',
          label: i18n.t('page.accounting.labels.qrCodeSettingsTitle'),
          href: settingsRoutes.qrCodeSettings,
        })
        .whenFeatureEnabled(featureFlags.SALES_PRE_ACCOUNTING, {
          id: 'preAccounting',
          label: i18n.t('page.accountingSettings.labels.preAccountingProcessing'),
          items: [
            {
              id: 'accountingCodes',
              label: i18n.t('page.accountingSettings.labels.accountingCodes'),
              href: settingsRoutes.accountingCodes,
            },
            {
              id: 'invoiceCostGeneration',
              label: i18n.t('page.accountingSettings.labels.invoiceCostGeneration'),
              href: settingsRoutes.invoiceCostOverview,
            },
            {
              id: 'accountingOperationDefaultsOverview',
              label: i18n.t('page.accounting.labels.operationDefaults.title'),
              href: settingsRoutes.accountingOperationDefaultsOverview,
            },
            {
              id: 'operationAuthorizations',
              label: i18n.t('page.accounting.labels.operationAuthorizationsOverview'),
              href: settingsRoutes.accountingOperationAuthorizationsOverview,
            },
            {
              id: 'preProcessingCalculation',
              label: i18n.t('page.accounting.labels.preProcessingCalculation'),
              href: settingsRoutes.preAccountingProcessingOverview,
            },
            {
              id: 'accountingErrorLog',
              label: i18n.t('page.accounting.labels.accountingErrorLog'),
              href: settingsRoutes.accountingErrorLog,
            },
            {
              id: 'accountingMatrixManagement',
              label: i18n.t('page.accounting.labels.accountingMatrixManagement'),
              href: settingsRoutes.accountingMatrixManagement,
            },
            {
              id: 'accountingActivityLog',
              label: i18n.t('page.accounting.labels.accountingActivityLog'),
              href: settingsRoutes.accountingActivityLog,
            },
            {
              id: 'accountingIntegrations',
              label: i18n.t('page.accountingSettings.labels.integrations'),
              href: settingsRoutes.accountingIntegrations,
            },
          ],
        }),
    })
    .add({
      id: 'integratedServices',
      label: i18n.t('page.integrations.labels.title'),
      leftIcon: 'action/extension',
      items: buildArray<NavigationItem>([
        {
          id: 'vehicleServices',
          label: i18n.t('page.integrations.labels.vehicleServices'),
          href: settingsRoutes.vehicleServices,
        },
        {
          id: 'smsGateways',
          label: i18n.t('page.integrations.labels.smsGateways'),
          href: settingsRoutes.smsGateways,
        },
        {
          id: 'emailServices',
          label: i18n.t('page.integrations.labels.emailServices'),
          href: settingsRoutes.emailServices,
        },
        {
          id: 'customerVerificationSerivces',
          label: i18n.t('page.integrations.labels.customerVerification'),
          href: settingsRoutes.customerVerificationServices,
        },
      ])
        .whenFeatureEnabled(featureFlags.WHATSAPP_INTEGRATION, {
          id: 'whatsApp',
          label: i18n.t('page.integrations.labels.whatsApp'),
          href: settingsRoutes.whatsApp,
        })
        .when(isFeatureEnabled(featureFlags.SETTINGS_FACEBOOK) && canReadFacebookLeads, {
          id: 'facebookCredentials',
          label: i18n.t('page.integrations.labels.facebookCredentials'),
          href: settingsRoutes.facebookCredentials,
        })
        .whenFeatureEnabled(featureFlags.DIGITAL_SIGNATURE, {
          id: 'esigno',
          label: i18n.t('page.settings.labels.digitalSignature'),
          href: settingsRoutes.esignoDetail,
        })
        .when(
          isFeatureEnabled(featureFlags.AUTOSTACJA_CUSTOMER_WIDGET) ||
            isFeatureEnabled(featureFlags.SETTINGS_JBR),
          {
            id: 'externalSystems',
            label: i18n.t('page.integrations.labels.externalSystems'),
            items: buildArray<NavigationItem>()
              .whenFeatureEnabled(featureFlags.AUTOSTACJA_CUSTOMER_WIDGET, {
                id: 'autostacjaWidget',
                label: i18n.t('page.integrations.labels.autostacja'),
                href: settingsRoutes.autostacjaSettings,
              })
              .whenFeatureEnabled(featureFlags.SETTINGS_JBR, {
                id: 'jbr',
                label: i18n.t('page.integrations.labels.jbr'),
                href: settingsRoutes.jbr,
              }),
          }
        ),
    })
    .add({
      id: 'localization',
      label: i18n.t('general.localeConfig.localization'),
      leftIcon: 'action/language',
      href: settingsRoutes.locale,
    });

  const allItems = getMenuItems(menuItems);
  const activeItem = allItems.find((item) => item.href && pathname.includes(item.href));

  const handleItemChange = (id: string) => {
    const item = allItems.find((item) => item.id === id);
    if (item?.href) {
      navigate(item.href);
    }
  };

  return (
    <Menu
      items={menuItems}
      activeItemId={activeItem?.id}
      onItemChange={handleItemChange}
      isSearchable
      data-testid="menu"
    />
  );
}

const getMenuItems = (items: NavigationItem[]): NavigationItem[] =>
  items.flatMap((item) => (item.items ? getMenuItems(item.items) : item));
